import { PouchDoc } from './pouch';
import { Log_DayStart } from './log-day-start';
import { Log_Meal } from './log-meal';
import { Log_WeighIn } from './log-weigh-in';
import { Log_Workout } from './log-workout';
import { Log_WeeklyCheckup } from './log-weekly-checkup';
import { DateTime, FixedOffsetZone } from 'luxon';

export enum LogRecordType {
  DayStart = 'DayStart' /** Placeholder for nutrient targets and similar values that are per-day. */,
  Workout = 'Workout',
  WeighIn = 'WeighIn',
  Meal = 'Meal',
  DayEnd = 'DayEnd',
  WeeklyCheckup = 'WeeklyCheckup', /** Weekly checkup, used to store weekly summary of the week */
}

export enum LogRecordState {
  Scheduled = 'Scheduled', /** all items are created in this state */
  Planned = 'Planned', /** meals get into this state when user decides what to eat there */
  //... we could put 'Prepared' here for meals that were cooked already
  Incomplete = 'Incomplete', /** used for workouts and workout sets that are in-progress */
  Done = 'Done', /** this item is done */
  Skipped = 'Skipped', /** user decided to skip this for some reason */
}

export interface Log_EntryBase extends PouchDoc {
  type: LogRecordType;
  // current state of this record
  state: LogRecordState;
  // time when this was logged as happened (should be when it happened, but one can never really trust the user for such things)
  time_utc: string; // ISO8601
  timeOffset_min: number; // offset in minutes from UTC
  /**
   * Entry name - depends on type:
   * - Workouts: "Workout A/B/C..." - use WorkoutLoaderService.getEffectiveWorkoutName() to read, setWorkoutName() to write
   * - Meals: "dinner", "breakfast", etc.
   * - Names beginning with $ are hidden system entries (e.g., $intake)
   */
  name?: string;
}
export interface Log_DayEnd extends Log_EntryBase {
  type: LogRecordType.DayEnd;
}

export type Log_Entry = Log_DayStart | Log_DayEnd | Log_Meal | Log_WeighIn | Log_Workout | Log_WeeklyCheckup;

export function isWeeklyCheckupEntry(entry: Log_Entry): entry is Log_WeeklyCheckup {
  return entry.type === LogRecordType.WeeklyCheckup;
}

export function isDayLogEntry(entry: Log_Entry): entry is Log_Entry {
  return 'type' in entry;
}

// the set of valid log-entry type strings, built once from the enum.
const LOG_RECORD_TYPES: ReadonlySet<string> = new Set<string>(Object.values(LogRecordType));

/**
 * Strong log-entry guard: true only when `doc.type` is a real {@link LogRecordType} value.
 *
 * This is deliberately stronger and more self-documenting than {@link isDayLogEntry}
 * (which only checks that a `type` field is present). It exists to exclude journal
 * snapshots that share the `log/` id prefix but are NOT log entries: plan backups
 * (`log/<ts>-FitnessPlan`) and intake-form snapshots (`log/<ts>-IntakeForm`) carry no
 * `type`, so they are never a real `LogRecordType`, yet a `log/`-prefix `_all_docs`
 * range query sweeps them up alongside real entries. Filtering a raw range result with
 * this predicate keeps those non-entries out of the log-entry pipeline, so a missing
 * `time_utc` is only ever reported for a doc that truly is a log entry. (A plan backup
 * in particular also has no `time_utc`, which is what made the coach-side time check
 * mis-report it.) Accepts `unknown` because the caller's raw range read may contain such
 * non-entry docs (and `null`/`undefined`).
 */
export function isLogRecordEntry(doc: unknown): doc is Log_Entry {
  const type = (doc as { type?: unknown } | null | undefined)?.type;
  return typeof type === 'string' && LOG_RECORD_TYPES.has(type);
}

export function assureIdForEntry(le: Log_Entry) {
  if (le._id == null) {
    if (
      !DateTime.fromISO(le.time_utc).isValid ||
      ![LogRecordType.DayStart, LogRecordType.DayEnd, LogRecordType.Meal, LogRecordType.WeighIn, LogRecordType.Workout, LogRecordType.WeeklyCheckup].some(
        e => e === le.type,
      )
    ) {
      throw new Error(`Log entry invalid for saving: ${JSON.stringify(le, null, 2)}`);
    }
    le._id = logEntryIdFor(le.time_utc, le.type);
  }
}

/**
 * The id a log entry of `type` gets when it is created at `time_utc`. An entry keeps this id for
 * life, even after it is moved to another time, so a moved entry's id names where it USED to be.
 */
export function logEntryIdFor(time_utc: string, type: LogRecordType): string {
  return 'log/' + time_utc + '-' + type;
}

/**
 * The id a fresh entry at `time_utc` should take when some of the ids around it are already held.
 * Answers the natural id when it is free; otherwise moves only the id forward one millisecond at a
 * time until it is free. The entry's own time is not touched, so it still sits where it should.
 * A moved entry keeps the id of its old time, so a fresh entry's natural id can belong to another
 * entry, and two entries with one id lose one of them (#4641). Why this shape rather than a suffix:
 * design-docs/sync-integrity.md, "A Fresh Entry Must Never Take an Id Another Entry Holds".
 */
export function firstFreeLogEntryId(time_utc: string, type: LogRecordType, takenIds: ReadonlySet<string>): string {
  let idTime_utc = DateTime.fromISO(time_utc, { zone: 'utc' });
  let id = logEntryIdFor(time_utc, type);
  while (takenIds.has(id)) {
    idTime_utc = idTime_utc.plus({ milliseconds: 1 });
    id = logEntryIdFor(idTime_utc.toISO(), type);
  }
  return id;
}

export function getEntryTimeFromID(id: string): DateTime {
  // parse the time from the id, id is in format log/YYYY-MM-DDTHH:MM:SS.sssZ-<type>
  let timeString = id.match(/log\/([\d\-:T\.]+Z)-/)[1];
  return DateTime.fromISO(timeString);
}

export function createGenericEntry(date_local: DateTime, type: LogRecordType, name?: string): Log_Entry {
  let entry = {} as Log_Entry;
  if (name != null) {
    entry.name = name;
  }
  entry.time_utc = date_local.toUTC().toISO();
  entry.timeOffset_min = date_local.offset;
  entry.type = type;
  entry.state = LogRecordState.Scheduled;

  assureIdForEntry(entry);
  return entry;
}

export function getEntryLocalTimeInDesiredTimeZone(entry: Pick<Log_Entry, 'time_utc' | 'timeOffset_min'>, desiredTime: DateTime): DateTime {
  return DateTime.fromISO(entry.time_utc).setZone(FixedOffsetZone.instance(entry.timeOffset_min)).setZone(desiredTime.zone, { keepLocalTime: true })
}

/**
 * Move an entry to a new local time. Writes BOTH `time_utc` and `timeOffset_min`.
 *
 * The two fields only mean something together — read as a pair by
 * {@link getEntryLocalTimeInDesiredTimeZone}, of which this is the exact inverse — so writing
 * one without the other leaves the entry reading as a time nobody scheduled. Never assign
 * `time_utc` by hand; use this or {@link copyEntryLocalTime}.
 *
 * See `design-docs/sync-integrity.md`, "A Log Entry's Two Time Fields Must Always Move
 * Together", for what that breaks and how it reached a customer (#4728).
 */
export function setEntryLocalTime(entry: Log_Entry, time_local: DateTime) {
  entry.time_utc = time_local.toUTC().toISO();
  entry.timeOffset_min = time_local.offset;
}

/**
 * Move `target` to wherever `source` sits, carrying both time fields across as a pair.
 * The copy-from-another-entry form of {@link setEntryLocalTime}; see there for why both
 * fields must always travel together.
 */
export function copyEntryLocalTime(target: Log_Entry, source: Log_Entry) {
  target.time_utc = source.time_utc;
  target.timeOffset_min = source.timeOffset_min;
}
